import { GordonFoodServiceInvoiceRecord } from "client/jspPlatformExperiment";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
// eslint-disable-next-line max-len
import { GFSInvoiceItemActionState } from "components/food/ui/supplierInfo/invoiceUpdateSupplier/GFSInvoiceItemActionBox";

export interface InvoiceUpdateSupplierInfoStateProps {
  actionItems: Record<
    string,
    { data: GordonFoodServiceInvoiceRecord; state: GFSInvoiceItemActionState }
  >;
  resolvedInvoices: Record<string, GordonFoodServiceInvoiceRecord>;
  isLoading: boolean;
  error: string | null;
}

const initialState: InvoiceUpdateSupplierInfoStateProps = {
  actionItems: {},
  resolvedInvoices: {},
  isLoading: false,
  error: null,
};

const updateAnActionItem = (
  state: InvoiceUpdateSupplierInfoStateProps,
  action: PayloadAction<{
    key: string;
    newData: GFSInvoiceItemActionState;
  } | null>,
): InvoiceUpdateSupplierInfoStateProps => {
  if (!action.payload) {
    return state;
  }
  return {
    ...state,
    actionItems: {
      ...state.actionItems,
      [action.payload.key]: {
        ...state.actionItems[action.payload.key],
        state: action.payload.newData,
      },
    },
  };
};

const init = (
  state: InvoiceUpdateSupplierInfoStateProps,
  action: PayloadAction<{
    actionItems: GordonFoodServiceInvoiceRecord[];
    resolvedInvoices: GordonFoodServiceInvoiceRecord[];
  } | null>,
) => {
  if (!action.payload) {
    return initialState;
  }

  const actionItems: Record<
    string,
    { data: GordonFoodServiceInvoiceRecord; state: GFSInvoiceItemActionState }
  > = {};
  const resolvedInvoices: Record<string, GordonFoodServiceInvoiceRecord> = {};
  action.payload.actionItems.forEach((invoice) => {
    actionItems[uuidv4()] = {
      data: invoice,
      state: {
        chooseAction: undefined,
        actionMsg: "Waiting action to finish ...",
      },
    };
  });

  action.payload.resolvedInvoices.forEach((invoice) => {
    resolvedInvoices[uuidv4()] = invoice;
  });

  return {
    ...state,
    actionItems,
    resolvedInvoices,
  };
};

const postInvoiceCSVFile = createAsyncThunk(
  "invoiceUpdateSupplierInfo/postInvoiceCSVFile",
  async (
    input: { invoiceCSVFile: File | Blob; supplierId: string },
    { signal, rejectWithValue },
  ) => {
    return jspPlatformApi()
      .ingredient.postFoodSupplierInfoInvoice({
        body: { invoice_csv_file: input.invoiceCSVFile },
        query: { supplier_name: input.supplierId },
        signal,
      })
      .then((res) => {
        const [toSolve, solved] = res.data ?? [[], []];
        return {
          actionItems: toSolve,
          resolvedInvoices: solved,
        };
      })
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      });
  },
);

export const invoiceUpdateSupplierInfoSlice = createSlice({
  name: "invoiceUpdateSupplierInfoSlice",
  initialState,
  reducers: {
    init,
    updateAnActionItem,
    clearError: (state) => ({ ...state, error: null }),
  },
  extraReducers: (builder) => {
    builder.addCase(postInvoiceCSVFile.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(postInvoiceCSVFile.fulfilled, (state, action) => {
      return { ...state, ...init(state, action), isLoading: false };
    });
    builder.addCase(postInvoiceCSVFile.rejected, (state, action) => {
      return { ...state, isLoading: false, error: action.payload as string };
    });
  },
});

export const invoiceUpdateSupplierInfoActions = {
  ...invoiceUpdateSupplierInfoSlice.actions,
  postInvoiceCSVFile,
};

const invoiceUpdateSupplierInfoReducer = invoiceUpdateSupplierInfoSlice.reducer;

export default invoiceUpdateSupplierInfoReducer;
